import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Search,
  Truck,
  ShoppingCart,
  Receipt,
  Package,
  Plus,
  Trash2,
  Loader2,
  X,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent } from '@components/Card';
import { Dialog } from '@components/Dialog';
import { DateInputBR } from '@components/DateInputBR';
import { useAuth } from '@contexts/AuthContext';
import { useCepSearch } from '@/hooks/useCepSearch';
import { goodsPickupOrderService } from '../../services/goodsPickupOrderService';
import { providerService } from '../../services/providerService';
import {
  GoodsPickupOrderFormData,
  GoodsPickupOrderItemFormData,
} from '../../types/goodsPickupOrder';
import { Provider } from '../../types/provider';
import { BRAZILIAN_STATES } from '../../types/provider';

// A retirada só aceita quantidades inteiras (Financial.GoodsPickupOrdersItem.Quantity é int).
// Itens de matéria-prima em um pedido de compra podem ter quantidade fracionária (ex.: 7.06 kg);
// ao copiar para a retirada, arredondamos para a unidade inteira mais próxima (mínimo 1).
const toWholeQuantity = (quantity: number | undefined | null): number =>
  Math.max(1, Math.round(quantity ?? 1));

export const GoodsPickupOrderFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const { user } = useAuth();
  const { searchCep, isLoading: cepLoading } = useCepSearch();

  // ─── Form state ────────────────────────────────────────────
  const [description, setDescription] = useState('');
  const [providerId, setProviderId] = useState<number | null>(null);
  const [providerName, setProviderName] = useState('');
  const [purchaseOrderId, setPurchaseOrderId] = useState<number | null>(null);
  const [purchaseOrderLabel, setPurchaseOrderLabel] = useState('');
  const [orderId, setOrderId] = useState<number | null>(null);
  const [orderLabel, setOrderLabel] = useState('');
  const [pickupContactName, setPickupContactName] = useState('');
  const [shipAddrZip, setShipAddrZip] = useState('');
  const [shipAddrStreet, setShipAddrStreet] = useState('');
  const [shipAddrNum, setShipAddrNum] = useState('');
  const [shipAddrCompl, setShipAddrCompl] = useState('');
  const [shipAddrDistrict, setShipAddrDistrict] = useState('');
  const [shipAddrCity, setShipAddrCity] = useState('');
  const [shipAddrState, setShipAddrState] = useState('');
  const [volumeQuantity, setVolumeQuantity] = useState<string>('');
  const [pickupDeadlineDate, setPickupDeadlineDate] = useState('');
  const [items, setItems] = useState<GoodsPickupOrderItemFormData[]>([]);

  // ─── UI state ──────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ─── Field-level validation errors ────────────────────────
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // ─── Refs for scroll-to-field ─────────────────────────────
  const refDescription = useRef<HTMLTextAreaElement>(null);
  const refPickupDeadlineDate = useRef<HTMLInputElement>(null);
  const refShipAddrZip = useRef<HTMLInputElement>(null);
  const refShipAddrStreet = useRef<HTMLInputElement>(null);
  const refShipAddrNum = useRef<HTMLInputElement>(null);
  const refShipAddrDistrict = useRef<HTMLInputElement>(null);
  const refShipAddrCity = useRef<HTMLInputElement>(null);
  const refShipAddrState = useRef<HTMLSelectElement>(null);

  // ─── Modal states ──────────────────────────────────────────
  const [showProviderSearch, setShowProviderSearch] = useState(false);
  const [showPurchaseOrderSearch, setShowPurchaseOrderSearch] = useState(false);
  const [showSalesOrderSearch, setShowSalesOrderSearch] = useState(false);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [showItemSelection, setShowItemSelection] = useState(false);

  // ─── Provider search ──────────────────────────────────────
  const [providerQuery, setProviderQuery] = useState('');
  const [providerResults, setProviderResults] = useState<Provider[]>([]);
  const [providerSearching, setProviderSearching] = useState(false);

  // ─── Purchase order search ────────────────────────────────
  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([]);
  const [poSearching, setPoSearching] = useState(false);

  // ─── Sales order search ───────────────────────────────────
  const [salesOrderQuery, setSalesOrderQuery] = useState('');
  const [salesOrderResults, setSalesOrderResults] = useState<any[]>([]);
  const [soSearching, setSoSearching] = useState(false);

  // ─── Product search ───────────────────────────────────────
  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState<any[]>([]);
  const [productSearching, setProductSearching] = useState(false);

  // ─── Item selection from PO/SO ────────────────────────────
  const [selectionItems, setSelectionItems] = useState<any[]>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<number>>(new Set());
  const [selectedItemQuantities, setSelectedItemQuantities] = useState<Record<number, number>>({});
  const [selectionSource, setSelectionSource] = useState<'po' | 'so'>('po');
  const [selectionHasFractionalQty, setSelectionHasFractionalQty] = useState(false);

  // ─── Load existing order for edit ─────────────────────────
  useEffect(() => {
    if (isEditing && id) {
      loadOrder(parseInt(id));
    }
  }, [id]);

  const loadOrder = async (orderId: number) => {
    try {
      setLoading(true);
      const order = await goodsPickupOrderService.getOrderById(orderId);
      setDescription(order.description);
      setProviderId(order.provider_id || null);
      setProviderName(order.provider_name || '');
      setPurchaseOrderId(order.purchase_order_id || null);
      setPurchaseOrderLabel(order.purchase_order_id ? `PC #${order.purchase_order_id}` : '');
      setOrderId(order.order_id || null);
      setOrderLabel(order.order_id ? `PV #${order.order_id}` : '');
      setPickupContactName(order.pickup_contact_name || '');
      setShipAddrZip(order.ship_addr_zip || '');
      setShipAddrStreet(order.ship_addr_street || '');
      setShipAddrNum(order.ship_addr_num || '');
      setShipAddrCompl(order.ship_addr_compl || '');
      setShipAddrDistrict(order.ship_addr_district || '');
      setShipAddrCity(order.ship_addr_city || '');
      setShipAddrState(order.ship_addr_state || '');
      setVolumeQuantity(order.volume_quantity?.toString() || '');
      setPickupDeadlineDate(order.pickup_deadline_date ? order.pickup_deadline_date.split('T')[0] : '');
      setItems(order.items?.map(i => ({
        sku: i.sku,
        quantity: i.quantity,
        description: i.description,
      })) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar ordem');
    } finally {
      setLoading(false);
    }
  };

  // ─── CEP lookup ───────────────────────────────────────────
  const handleCepSearch = async () => {
    const result = await searchCep(shipAddrZip);
    if (result) {
      setShipAddrStreet(result.logradouro);
      setShipAddrDistrict(result.bairro);
      setShipAddrCity(result.localidade);
      setShipAddrState(result.uf);
    }
  };

  useEffect(() => {
    const cleaned = shipAddrZip.replace(/\D/g, '');
    if (cleaned.length === 8) {
      handleCepSearch();
    }
  }, [shipAddrZip]);

  // ─── Provider search handler ──────────────────────────────
  const handleProviderSearch = async (query: string) => {
    setProviderQuery(query);
    if (!query.trim()) {
      setProviderResults([]);
      return;
    }
    setProviderSearching(true);
    try {
      const results = await providerService.searchProviders(query);
      setProviderResults(Array.isArray(results) ? results : []);
    } catch {
      setProviderResults([]);
    } finally {
      setProviderSearching(false);
    }
  };

  const handleSelectProvider = (provider: Provider) => {
    setProviderId(provider.provider_id);
    setProviderName(provider.trade_name || provider.legal_name);
    // Auto-fill address from provider
    if (provider.addr_zip) setShipAddrZip(provider.addr_zip);
    if (provider.addr_street) setShipAddrStreet(provider.addr_street);
    if (provider.addr_num) setShipAddrNum(provider.addr_num);
    if (provider.addr_compl) setShipAddrCompl(provider.addr_compl);
    if (provider.addr_district) setShipAddrDistrict(provider.addr_district);
    if (provider.addr_city) setShipAddrCity(provider.addr_city);
    if (provider.addr_state) setShipAddrState(provider.addr_state);
    // Clear purchase order if provider changed
    setPurchaseOrderId(null);
    setPurchaseOrderLabel('');
    setShowProviderSearch(false);
    setProviderQuery('');
    setProviderResults([]);
  };

  const handleClearProvider = () => {
    setProviderId(null);
    setProviderName('');
    setPurchaseOrderId(null);
    setPurchaseOrderLabel('');
  };

  // ─── Purchase order search ────────────────────────────────
  const handleOpenPurchaseOrderSearch = async () => {
    if (!providerId) return;
    setPoSearching(true);
    setShowPurchaseOrderSearch(true);
    try {
      const pos = await goodsPickupOrderService.getOpenPurchaseOrdersByProvider(providerId);
      setPurchaseOrders(pos);
    } catch {
      setPurchaseOrders([]);
    } finally {
      setPoSearching(false);
    }
  };

  const handleSelectPurchaseOrder = async (po: any) => {
    setPurchaseOrderId(po.purchase_order_id);
    setPurchaseOrderLabel(`PC #${po.purchase_order_id} — ${po.order_number || ''}`);
    setShowPurchaseOrderSearch(false);
    // Load PO items for selection
    try {
      const poDetail = await goodsPickupOrderService.getPurchaseOrderItems(po.purchase_order_id);
      if (poDetail.length > 0) {
        // Itens de matéria-prima do pedido de compra podem ter quantidade fracionária (kg, etc.);
        // a retirada só aceita inteiros — arredondamos aqui e avisamos o usuário.
        setSelectionHasFractionalQty(poDetail.some((item: any) => !Number.isInteger(item.quantity)));
        setSelectionItems(poDetail.map((item: any, idx: number) => ({
          _id: idx,
          sku: item.sku || item.product_id?.toString() || `ITEM-${idx + 1}`,
          description: item.description || '',
          quantity: toWholeQuantity(item.quantity),
        })));
        setSelectedItemIds(new Set(poDetail.map((_: any, idx: number) => idx)));
        setSelectedItemQuantities(Object.fromEntries(poDetail.map((item: any, idx: number) => [idx, toWholeQuantity(item.quantity)])));
        setSelectionSource('po');
        setShowItemSelection(true);
      }
    } catch {
      // Items are optional
    }
  };

  // ─── Sales order search ───────────────────────────────────
  const handleSalesOrderSearch = async (query: string) => {
    setSalesOrderQuery(query);
    if (!query.trim()) {
      setSalesOrderResults([]);
      return;
    }
    setSoSearching(true);
    try {
      const results = await goodsPickupOrderService.searchSalesOrders(query);
      setSalesOrderResults(results);
    } catch {
      setSalesOrderResults([]);
    } finally {
      setSoSearching(false);
    }
  };

  const handleSelectSalesOrder = async (so: any) => {
    setOrderId(so.order_id);
    setOrderLabel(`PV #${so.order_id} — ${so.order_num_erp || ''} — ${so.customer_name || ''}`);
    // Auto-fill address from sales order
    if (so.ship_addr_zip) setShipAddrZip(so.ship_addr_zip);
    if (so.ship_addr_street) setShipAddrStreet(so.ship_addr_street);
    if (so.ship_addr_num) setShipAddrNum(so.ship_addr_num);
    if (so.ship_addr_compl) setShipAddrCompl(so.ship_addr_compl);
    if (so.ship_addr_district) setShipAddrDistrict(so.ship_addr_district);
    if (so.ship_addr_city) setShipAddrCity(so.ship_addr_city);
    if (so.ship_addr_state) setShipAddrState(so.ship_addr_state);
    setShowSalesOrderSearch(false);
    setSalesOrderQuery('');
    setSalesOrderResults([]);
    // Load SO items for selection
    try {
      const soDetail = await goodsPickupOrderService.getSalesOrderById(so.order_id);
      const soItems = soDetail?.items || [];
      if (soItems.length > 0) {
        setSelectionHasFractionalQty(false);
        setSelectionItems(soItems.map((item: any, idx: number) => ({
          _id: idx,
          sku: item.sku,
          description: item.description || '',
          quantity: item.quantity || 1,
        })));
        setSelectedItemIds(new Set(soItems.map((_: any, idx: number) => idx)));
        setSelectedItemQuantities(Object.fromEntries(soItems.map((item: any, idx: number) => [idx, item.quantity || 1])));
        setSelectionSource('so');
        setShowItemSelection(true);
      }
    } catch {
      // Items are optional
    }
  };

  const handleClearSalesOrder = () => {
    setOrderId(null);
    setOrderLabel('');
  };

  // ─── Item selection confirm ───────────────────────────────
  const handleConfirmItemSelection = () => {
    const selected = selectionItems
      .map((item: any, idx: number) => ({ item, idx }))
      .filter(({ idx }) => selectedItemIds.has(idx))
      .map(({ item, idx }) => ({
        sku: item.sku,
        quantity: toWholeQuantity(selectedItemQuantities[idx] ?? item.quantity),
        description: item.description,
      }));
    setItems(prev => [...prev, ...selected]);
    setShowItemSelection(false);
    setSelectionItems([]);
    setSelectedItemIds(new Set());
    setSelectedItemQuantities({});
    setSelectionHasFractionalQty(false);
  };

  const toggleItemSelection = (idx: number) => {
    setSelectedItemIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) newSet.delete(idx);
      else newSet.add(idx);
      return newSet;
    });
    setSelectedItemQuantities(prev => {
      if (prev[idx] == null) {
        return { ...prev, [idx]: selectionItems[idx]?.quantity || 1 };
      }
      return prev;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItemIds.size === selectionItems.length) {
      setSelectedItemIds(new Set());
    } else {
      setSelectedItemIds(new Set(selectionItems.map((_: any, idx: number) => idx)));
      setSelectedItemQuantities(prev => {
        const updated = { ...prev };
        selectionItems.forEach((item, idx) => {
          if (updated[idx] == null) updated[idx] = item.quantity || 1;
        });
        return updated;
      });
    }
  };

  // ─── Product search ───────────────────────────────────────
  const handleProductSearch = async (query: string) => {
    setProductQuery(query);
    if (!query.trim()) {
      setProductResults([]);
      return;
    }
    setProductSearching(true);
    try {
      const results = await goodsPickupOrderService.searchProducts(query);
      setProductResults(results);
    } catch {
      setProductResults([]);
    } finally {
      setProductSearching(false);
    }
  };

  const handleSelectProduct = (product: any) => {
    const newItem: GoodsPickupOrderItemFormData = {
      sku: product.sku || product.product_id?.toString() || '',
      quantity: 1,
      description: product.description || product.name || '',
    };
    setItems(prev => [...prev, newItem]);
    setShowProductSearch(false);
    setProductQuery('');
    setProductResults([]);
  };

  // ─── Remove item ──────────────────────────────────────────
  const handleRemoveItem = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleItemQuantityChange = (index: number, quantity: number) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, quantity } : item));
  };

  // ─── Save ─────────────────────────────────────────────────
  const handleSave = async () => {
    console.log('[GoodsPickupOrderForm] handleSave triggered');

    // ─── Build per-field error map ─────────────────────────
    const errors: Record<string, string> = {};

    if (!description.trim()) {
      errors.description = 'A descrição é obrigatória';
    }
    if (!pickupDeadlineDate) {
      errors.pickupDeadlineDate = 'A data limite é obrigatória';
    }
    if (!shipAddrZip.replace(/\D/g, '')) {
      errors.shipAddrZip = 'O CEP é obrigatório';
    }
    if (!shipAddrStreet.trim()) {
      errors.shipAddrStreet = 'A rua é obrigatória';
    }
    if (!shipAddrNum.trim()) {
      errors.shipAddrNum = 'O número é obrigatório';
    }
    if (!shipAddrDistrict.trim()) {
      errors.shipAddrDistrict = 'O bairro é obrigatório';
    }
    if (!shipAddrCity.trim()) {
      errors.shipAddrCity = 'A cidade é obrigatória';
    }
    if (!shipAddrState.trim()) {
      errors.shipAddrState = 'Selecione o estado';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);

      // Scroll to the first invalid field in document order
      const firstRef = [
        { key: 'description', ref: refDescription },
        { key: 'pickupDeadlineDate', ref: refPickupDeadlineDate },
        { key: 'shipAddrZip', ref: refShipAddrZip },
        { key: 'shipAddrStreet', ref: refShipAddrStreet },
        { key: 'shipAddrNum', ref: refShipAddrNum },
        { key: 'shipAddrDistrict', ref: refShipAddrDistrict },
        { key: 'shipAddrCity', ref: refShipAddrCity },
        { key: 'shipAddrState', ref: refShipAddrState },
      ].find(({ key }) => errors[key]);

      if (firstRef?.ref.current) {
        firstRef.ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstRef.ref.current.focus();
      }

      console.log('[GoodsPickupOrderForm] validation failed:', errors);
      return;
    }

    setFieldErrors({});
    console.log('[GoodsPickupOrderForm] validation passed, calling API...');

    try {
      setSaving(true);
      setError(null);

      const formData: GoodsPickupOrderFormData = {
        description: description.trim(),
        provider_id: providerId,
        purchase_order_id: purchaseOrderId,
        pickup_contact_name: pickupContactName.trim() || undefined,
        order_id: orderId,
        ship_addr_street: shipAddrStreet.trim(),
        ship_addr_num: shipAddrNum.trim(),
        ship_addr_compl: shipAddrCompl.trim(),
        ship_addr_district: shipAddrDistrict.trim(),
        ship_addr_city: shipAddrCity.trim(),
        ship_addr_state: shipAddrState.trim(),
        ship_addr_zip: shipAddrZip.replace(/\D/g, ''),
        volume_quantity: volumeQuantity ? parseInt(volumeQuantity) : undefined,
        pickup_deadline_date: pickupDeadlineDate,
        items: items.length > 0 ? items : undefined,
      };

      console.log('[GoodsPickupOrderForm] formData to submit:', formData);

      if (isEditing) {
        await goodsPickupOrderService.updateOrder(parseInt(id!), formData);
      } else {
        await goodsPickupOrderService.createOrder(formData);
      }

      console.log('[GoodsPickupOrderForm] saved successfully, navigating...');
      navigate('/goods-pickup-orders');
    } catch (err) {
      console.error('[GoodsPickupOrderForm] save error:', err);
      setError(err instanceof Error ? err.message : 'Erro ao salvar ordem de retirada');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-slate-500">Carregando ordem de retirada...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          icon={<ArrowLeft size={20} />}
          onClick={() => navigate('/goods-pickup-orders')}
        >
          Voltar
        </Button>
        <div>
          <h1 className="text-h1">
            {isEditing ? 'Editar Ordem de Retirada' : 'Nova Ordem de Retirada'}
          </h1>
          <p className="text-slate-600">
            {isEditing
              ? 'Altere os dados da ordem de retirada'
              : 'Preencha os dados para solicitar uma retirada de mercadorias'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Solicitante (readonly) */}
      <Card>
        <CardHeader title="Solicitante" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">E-mail do solicitante</label>
              <input
                type="text"
                className="input-field bg-slate-50"
                value={user?.email || ''}
                disabled
                readOnly
              />
              <p className="text-xs text-slate-500 mt-1">Preenchido automaticamente pelo sistema</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Descrição */}
      <Card>
        <CardHeader title="Dados da Retirada" />
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Descrição da retirada <span className="text-red-500">*</span>
              </label>
              <textarea
                ref={refDescription}
                className={`input-field min-h-[80px] ${fieldErrors.description ? 'border-red-400 focus:ring-red-400' : ''}`}
                placeholder="Descreva a finalidade da retirada (ex: retirada de produtos do fornecedor, devolução de cliente...)"
                value={description}
                onChange={(e) => { setDescription(e.target.value); setFieldErrors(p => ({ ...p, description: '' })); }}
                maxLength={500}
              />
              {fieldErrors.description
                ? <p className="text-xs text-red-600 mt-1">{fieldErrors.description}</p>
                : <p className="text-xs text-slate-500 mt-1">{description.length}/500 caracteres</p>
              }
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Contato para retirada</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Nome do contato no local"
                  value={pickupContactName}
                  onChange={(e) => setPickupContactName(e.target.value)}
                  maxLength={100}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Quantidade de volumes</label>
                <input
                  type="number"
                  className="input-field"
                  placeholder="Número de volumes"
                  value={volumeQuantity}
                  onChange={(e) => setVolumeQuantity(e.target.value)}
                  min={0}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Data limite para retirada <span className="text-red-500">*</span>
              </label>
              <DateInputBR
                ref={refPickupDeadlineDate}
                className={`input-field max-w-xs ${fieldErrors.pickupDeadlineDate ? 'border-red-400 focus:ring-red-400' : ''}`}
                value={pickupDeadlineDate}
                onChange={(v) => { setPickupDeadlineDate(v); setFieldErrors(p => ({ ...p, pickupDeadlineDate: '' })); }}
              />
              {fieldErrors.pickupDeadlineDate && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.pickupDeadlineDate}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vínculos: Fornecedor / Pedido de Compra / Pedido de Venda */}
      <Card>
        <CardHeader title="Vínculos (opcional)" />
        <CardContent>
          <div className="space-y-4">
            {/* Fornecedor */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fornecedor</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  className="input-field flex-1 bg-slate-50"
                  value={providerName ? `${providerName} (ID: ${providerId})` : ''}
                  placeholder="Nenhum fornecedor selecionado"
                  readOnly
                />
                <Button
                  variant="outline"
                  icon={<Search size={16} />}
                  onClick={() => setShowProviderSearch(true)}
                >
                  Buscar
                </Button>
                {providerId && (
                  <Button
                    variant="outline"
                    icon={<X size={16} />}
                    onClick={handleClearProvider}
                    className="text-red-500"
                  />
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Ao selecionar um fornecedor, o endereço será preenchido automaticamente
              </p>
            </div>

            {/* Pedido de Compra (só aparece se tiver fornecedor) */}
            {providerId && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Pedido de Compra</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="input-field flex-1 bg-slate-50"
                    value={purchaseOrderLabel}
                    placeholder="Nenhum pedido de compra selecionado"
                    readOnly
                  />
                  <Button
                    variant="outline"
                    icon={<ShoppingCart size={16} />}
                    onClick={handleOpenPurchaseOrderSearch}
                  >
                    Selecionar
                  </Button>
                  {purchaseOrderId && (
                    <Button
                      variant="outline"
                      icon={<X size={16} />}
                      onClick={() => { setPurchaseOrderId(null); setPurchaseOrderLabel(''); }}
                      className="text-red-500"
                    />
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Pedidos de compra com status "Em Aberto" do fornecedor selecionado
                </p>
              </div>
            )}

            {/* Pedido de Venda (devolução) */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Pedido de Venda (devolução de cliente)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  className="input-field flex-1 bg-slate-50"
                  value={orderLabel}
                  placeholder="Nenhum pedido de venda selecionado"
                  readOnly
                />
                <Button
                  variant="outline"
                  icon={<Receipt size={16} />}
                  onClick={() => setShowSalesOrderSearch(true)}
                >
                  Buscar
                </Button>
                {orderId && (
                  <Button
                    variant="outline"
                    icon={<X size={16} />}
                    onClick={handleClearSalesOrder}
                    className="text-red-500"
                  />
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Busque por nome do cliente ou número do pedido de venda
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Endereço de Retirada */}
      <Card>
        <CardHeader title="Endereço de Retirada" />
        <CardContent>
          <div className="space-y-4">
            {/* CEP com lupa */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  CEP <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    ref={refShipAddrZip}
                    type="text"
                    className={`input-field flex-1 ${fieldErrors.shipAddrZip ? 'border-red-400 focus:ring-red-400' : ''}`}
                    placeholder="00000-000"
                    value={shipAddrZip}
                    onChange={(e) => { setShipAddrZip(e.target.value.replace(/\D/g, '').slice(0, 8)); setFieldErrors(p => ({ ...p, shipAddrZip: '' })); }}
                    maxLength={9}
                  />
                  <Button
                    variant="outline"
                    icon={cepLoading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                    onClick={handleCepSearch}
                    disabled={cepLoading || shipAddrZip.replace(/\D/g, '').length !== 8}
                  />
                </div>
                {fieldErrors.shipAddrZip && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.shipAddrZip}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Rua <span className="text-red-500">*</span>
                </label>
                <input
                  ref={refShipAddrStreet}
                  type="text"
                  className={`input-field ${fieldErrors.shipAddrStreet ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder="Logradouro"
                  value={shipAddrStreet}
                  onChange={(e) => { setShipAddrStreet(e.target.value); setFieldErrors(p => ({ ...p, shipAddrStreet: '' })); }}
                  maxLength={100}
                />
                {fieldErrors.shipAddrStreet && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.shipAddrStreet}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Número <span className="text-red-500">*</span>
                </label>
                <input
                  ref={refShipAddrNum}
                  type="text"
                  className={`input-field ${fieldErrors.shipAddrNum ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder="Nº"
                  value={shipAddrNum}
                  onChange={(e) => { setShipAddrNum(e.target.value); setFieldErrors(p => ({ ...p, shipAddrNum: '' })); }}
                  maxLength={10}
                />
                {fieldErrors.shipAddrNum && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.shipAddrNum}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Complemento</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Complemento"
                  value={shipAddrCompl}
                  onChange={(e) => setShipAddrCompl(e.target.value)}
                  maxLength={100}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Bairro <span className="text-red-500">*</span>
                </label>
                <input
                  ref={refShipAddrDistrict}
                  type="text"
                  className={`input-field ${fieldErrors.shipAddrDistrict ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder="Bairro"
                  value={shipAddrDistrict}
                  onChange={(e) => { setShipAddrDistrict(e.target.value); setFieldErrors(p => ({ ...p, shipAddrDistrict: '' })); }}
                  maxLength={100}
                />
                {fieldErrors.shipAddrDistrict && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.shipAddrDistrict}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Cidade <span className="text-red-500">*</span>
                </label>
                <input
                  ref={refShipAddrCity}
                  type="text"
                  className={`input-field ${fieldErrors.shipAddrCity ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder="Cidade"
                  value={shipAddrCity}
                  onChange={(e) => { setShipAddrCity(e.target.value); setFieldErrors(p => ({ ...p, shipAddrCity: '' })); }}
                  maxLength={100}
                />
                {fieldErrors.shipAddrCity && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.shipAddrCity}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Estado <span className="text-red-500">*</span>
                </label>
                <select
                  ref={refShipAddrState}
                  className={`input-field ${fieldErrors.shipAddrState ? 'border-red-400 focus:ring-red-400' : ''}`}
                  value={shipAddrState}
                  onChange={(e) => { setShipAddrState(e.target.value); setFieldErrors(p => ({ ...p, shipAddrState: '' })); }}
                >
                  <option value="">Selecione...</option>
                  {BRAZILIAN_STATES.map(s => (
                    <option key={s.value} value={s.value}>{s.label} ({s.value})</option>
                  ))}
                </select>
                {fieldErrors.shipAddrState && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.shipAddrState}</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Itens */}
      <Card>
        <CardHeader title="Itens da Retirada (opcional)" />
        <CardContent>
          <p className="text-sm text-slate-500 mb-4">
            Adicione produtos que precisam ser retirados. Este campo é opcional quando a retirada não envolve produtos catalogados.
          </p>

          {items.length > 0 && (
            <div className="border border-slate-200 rounded-lg overflow-hidden mb-4">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs font-medium text-slate-600">SKU</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-slate-600">Descrição</th>
                    <th className="text-center px-4 py-2 text-xs font-medium text-slate-600">Qtd.</th>
                    <th className="text-center px-4 py-2 text-xs font-medium text-slate-600">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2">
                        <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">{item.sku}</code>
                      </td>
                      <td className="px-4 py-2 text-sm text-slate-700">{item.description || '—'}</td>
                      <td className="px-4 py-2 text-center">
                        <input
                          type="number"
                          className="input-field w-20 text-center text-sm mx-auto"
                          value={item.quantity}
                          onChange={(e) => handleItemQuantityChange(idx, parseInt(e.target.value) || 1)}
                          min={1}
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<Trash2 size={14} />}
                          onClick={() => handleRemoveItem(idx)}
                          className="text-red-500 hover:text-red-700"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Button
            variant="outline"
            icon={<Plus size={16} />}
            onClick={() => setShowProductSearch(true)}
          >
            Adicionar Produto
          </Button>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-col gap-3">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            ⚠ {error}
          </div>
        )}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/goods-pickup-orders')}>
            Cancelar
          </Button>
          <Button
            icon={saving ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar Ordem de Retirada'}
          </Button>
        </div>
      </div>


      {/* ═══════════════════════════════════════════════════════ */}
      {/* MODALS                                                  */}
      {/* ═══════════════════════════════════════════════════════ */}

      {/* Provider Search Modal */}
      <Dialog
        isOpen={showProviderSearch}
        onClose={() => { setShowProviderSearch(false); setProviderQuery(''); setProviderResults([]); }}
        title="Buscar Fornecedor"
        size="lg"
      >
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              className="w-full input-field pl-9 pr-8 py-2.5 text-sm"
              placeholder="Pesquisar por nome, CNPJ ou razão social..."
              value={providerQuery}
              onChange={(e) => handleProviderSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg">
            {providerSearching ? (
              <div className="flex items-center justify-center gap-2 py-8 text-slate-400 text-sm">
                <Loader2 size={16} className="animate-spin" /> Buscando fornecedores...
              </div>
            ) : providerResults.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {providerResults.map(provider => (
                  <button
                    key={provider.provider_id}
                    type="button"
                    className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors"
                    onClick={() => handleSelectProvider(provider)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 p-1.5 bg-slate-100 rounded-full">
                        <Truck size={14} className="text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 text-sm truncate">
                          {provider.trade_name || provider.legal_name}
                        </p>
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
                          <span className="text-xs text-slate-500">{provider.document}</span>
                          {provider.email && <span className="text-xs text-slate-500">{provider.email}</span>}
                        </div>
                        {provider.addr_city && (
                          <p className="text-xs text-slate-400 mt-0.5">
                            {provider.addr_street}{provider.addr_num ? `, ${provider.addr_num}` : ''}
                            {' — '}{provider.addr_city}/{provider.addr_state}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : providerQuery ? (
              <div className="py-8 text-center text-slate-400 text-sm">
                Nenhum fornecedor encontrado para "{providerQuery}"
              </div>
            ) : (
              <div className="py-8 text-center text-slate-400 text-sm">
                Digite para buscar fornecedores
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end pt-4 border-t border-slate-200 mt-4">
          <Button variant="outline" onClick={() => { setShowProviderSearch(false); setProviderQuery(''); setProviderResults([]); }}>
            Fechar
          </Button>
        </div>
      </Dialog>

      {/* Purchase Order Search Modal */}
      <Dialog
        isOpen={showPurchaseOrderSearch}
        onClose={() => setShowPurchaseOrderSearch(false)}
        title="Selecionar Pedido de Compra"
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Pedidos de compra em aberto do fornecedor: <strong>{providerName}</strong>
          </p>
          <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg">
            {poSearching ? (
              <div className="flex items-center justify-center gap-2 py-8 text-slate-400 text-sm">
                <Loader2 size={16} className="animate-spin" /> Buscando pedidos de compra...
              </div>
            ) : purchaseOrders.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {purchaseOrders.map(po => (
                  <button
                    key={po.purchase_order_id}
                    type="button"
                    className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors"
                    onClick={() => handleSelectPurchaseOrder(po)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900 text-sm">
                          PC #{po.purchase_order_id}
                          {po.order_number ? ` — Nº ${po.order_number}` : ''}
                        </p>
                        <p className="text-xs text-slate-500">
                          Data: {po.order_date ? new Date(po.order_date).toLocaleDateString('pt-BR') : '—'}
                          {po.total ? ` — Total: R$ ${Number(po.total).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : ''}
                        </p>
                      </div>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Em Aberto</span>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-slate-400 text-sm">
                Nenhum pedido de compra em aberto encontrado
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end pt-4 border-t border-slate-200 mt-4">
          <Button variant="outline" onClick={() => setShowPurchaseOrderSearch(false)}>Fechar</Button>
        </div>
      </Dialog>

      {/* Sales Order Search Modal */}
      <Dialog
        isOpen={showSalesOrderSearch}
        onClose={() => { setShowSalesOrderSearch(false); setSalesOrderQuery(''); setSalesOrderResults([]); }}
        title="Buscar Pedido de Venda"
        size="lg"
      >
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              className="w-full input-field pl-9 pr-8 py-2.5 text-sm"
              placeholder="Pesquisar por nome do cliente ou número do pedido..."
              value={salesOrderQuery}
              onChange={(e) => handleSalesOrderSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg">
            {soSearching ? (
              <div className="flex items-center justify-center gap-2 py-8 text-slate-400 text-sm">
                <Loader2 size={16} className="animate-spin" /> Buscando pedidos de venda...
              </div>
            ) : salesOrderResults.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {salesOrderResults.map(so => (
                  <button
                    key={so.order_id}
                    type="button"
                    className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors"
                    onClick={() => handleSelectSalesOrder(so)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900 text-sm">
                          PV #{so.order_id}
                          {so.order_num_erp ? ` — ERP ${so.order_num_erp}` : ''}
                        </p>
                        <p className="text-xs text-slate-500">
                          Cliente: {so.customer_name || '—'}
                        </p>
                        <p className="text-xs text-slate-400">
                          {so.ship_addr_city}{so.ship_addr_state ? `/${so.ship_addr_state}` : ''}
                          {so.order_date ? ` — ${new Date(so.order_date).toLocaleDateString('pt-BR')}` : ''}
                        </p>
                      </div>
                      {so.order_status && (
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                          {so.order_status}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : salesOrderQuery ? (
              <div className="py-8 text-center text-slate-400 text-sm">
                Nenhum pedido de venda encontrado para "{salesOrderQuery}"
              </div>
            ) : (
              <div className="py-8 text-center text-slate-400 text-sm">
                Digite para buscar pedidos de venda por nome do cliente ou número do pedido
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end pt-4 border-t border-slate-200 mt-4">
          <Button variant="outline" onClick={() => { setShowSalesOrderSearch(false); setSalesOrderQuery(''); setSalesOrderResults([]); }}>
            Fechar
          </Button>
        </div>
      </Dialog>

      {/* Item Selection Modal (PO or SO items) */}
      <Dialog
        isOpen={showItemSelection}
        onClose={() => setShowItemSelection(false)}
        title={selectionSource === 'po' ? 'Selecionar Itens do Pedido de Compra' : 'Selecionar Itens do Pedido de Venda'}
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Selecione os itens que devem ser incluídos na ordem de retirada:
          </p>
          {selectionHasFractionalQty && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Um ou mais itens do pedido de compra têm quantidade fracionária (ex.: matéria-prima
              vendida por peso). A retirada só aceita quantidades inteiras — os valores foram
              arredondados; ajuste manualmente se necessário.
            </p>
          )}
          <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-lg">
            <table className="w-full">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left">
                    <input
                      type="checkbox"
                      checked={selectedItemIds.size === selectionItems.length && selectionItems.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded"
                    />
                  </th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-slate-600">SKU</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-slate-600">Descrição</th>
                  <th className="text-center px-4 py-2 text-xs font-medium text-slate-600">Qtd.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {selectionItems.map((item, idx) => (
                  <tr key={idx} className={selectedItemIds.has(idx) ? 'bg-emerald-50' : ''}>
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        checked={selectedItemIds.has(idx)}
                        onChange={() => toggleItemSelection(idx)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">{item.sku}</code>
                    </td>
                    <td className="px-4 py-2 text-sm text-slate-700">{item.description || '—'}</td>
                    <td className="px-4 py-2 text-center">
                      <input
                        type="number"
                        className="w-20 text-center text-sm border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-400 disabled:bg-slate-100 disabled:text-slate-400 mx-auto block"
                        value={selectedItemIds.has(idx) ? (selectedItemQuantities[idx] ?? item.quantity) : item.quantity}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          if (!isNaN(val) && val >= 1) {
                            setSelectedItemQuantities(prev => ({ ...prev, [idx]: val }));
                          }
                        }}
                        min={1}
                        disabled={!selectedItemIds.has(idx)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500">
            {selectedItemIds.size} de {selectionItems.length} itens selecionados
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 mt-4">
          <Button variant="outline" onClick={() => { setShowItemSelection(false); setSelectionHasFractionalQty(false); }}>Cancelar</Button>
          <Button onClick={handleConfirmItemSelection} disabled={selectedItemIds.size === 0}>
            Confirmar Seleção ({selectedItemIds.size})
          </Button>
        </div>
      </Dialog>

      {/* Product Search Modal */}
      <Dialog
        isOpen={showProductSearch}
        onClose={() => { setShowProductSearch(false); setProductQuery(''); setProductResults([]); }}
        title="Buscar Produto"
        size="lg"
      >
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              className="w-full input-field pl-9 pr-8 py-2.5 text-sm"
              placeholder="Pesquisar por SKU, nome ou descrição..."
              value={productQuery}
              onChange={(e) => handleProductSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg">
            {productSearching ? (
              <div className="flex items-center justify-center gap-2 py-8 text-slate-400 text-sm">
                <Loader2 size={16} className="animate-spin" /> Buscando produtos...
              </div>
            ) : productResults.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {productResults.map((product: any) => (
                  <button
                    key={product.product_id || product.sku}
                    type="button"
                    className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors"
                    onClick={() => handleSelectProduct(product)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 p-1.5 bg-slate-100 rounded-full">
                        <Package size={14} className="text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 text-sm truncate">
                          {product.description || product.name}
                        </p>
                        <div className="flex gap-3 mt-0.5">
                          <span className="text-xs text-slate-500">SKU: {product.sku}</span>
                          {product.stock_quantity !== undefined && (
                            <span className="text-xs text-slate-500">Estoque: {product.stock_quantity}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : productQuery ? (
              <div className="py-8 text-center text-slate-400 text-sm">
                Nenhum produto encontrado para "{productQuery}"
              </div>
            ) : (
              <div className="py-8 text-center text-slate-400 text-sm">
                Digite para buscar produtos por SKU, nome ou descrição
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end pt-4 border-t border-slate-200 mt-4">
          <Button variant="outline" onClick={() => { setShowProductSearch(false); setProductQuery(''); setProductResults([]); }}>
            Fechar
          </Button>
        </div>
      </Dialog>
    </div>
  );
};
